import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaLeaf, FaClock } from 'react-icons/fa';
import { createQuote } from '../../utils/api';
import services from '../../utils/services';

const Hero = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState('');

  const badges = [
    { icon: FaShieldAlt, text: 'Licensed Technicians' },
    { icon: FaLeaf, text: 'Child and Pet Conscious Treatments' },
    { icon: FaClock, text: 'Fast Response Across Brisbane' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setServerError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setServerError('');

    try {
      await createQuote(formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });
    } catch (error) {
      const message = error.response?.data?.error || 'Unable to submit right now. Please try again.';
      setServerError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative bg-primary-500 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="container-custom relative z-10">
        <div className="flex flex-col lg:flex-row items-center lg:items-start min-h-[460px] pt-4 pb-8 md:pt-6 md:pb-10 gap-6 md:gap-8">
          {/* Left content */}
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block bg-accent-500 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              #1 Pest Control Service in Brisbane
            </span>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-4">
              Brisbane's Trusted
              <br />
              Pest Control Experts
            </h1>

            <p className="text-primary-100 text-base md:text-lg mb-5 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              From cockroaches and rodents to bed bugs - The Skilled Group
              delivers fast, reliable pest control backed by trained professionals across Brisbane.
              We also provide carpet, mattress, tile and grout, and couch steam cleaning
              for complete home care.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6">
              <Link to="/booking" className="btn-accent text-base py-3.5 px-10">
                Book Pest Control
              </Link>
              <Link to="/services" className="btn-outline border-white text-white hover:bg-white hover:text-primary-500 text-base py-3.5 px-10">
                Our Services
              </Link>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {badges.map((badge) => (
                <div
                  key={badge.text}
                  className="flex items-center gap-2 justify-center lg:justify-start"
                >
                  <badge.icon className="text-accent-400 flex-shrink-0" />
                  <span className="text-primary-100 text-sm font-medium">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right content — visual card */}
          <div className="flex-1 w-full max-w-md lg:max-w-lg">
            <div className="bg-white rounded-2xl shadow-2xl p-5 md:p-7">
              <h2 className="text-2xl font-heading font-bold text-primary-500 mb-2 text-center">
                Get a Free Quote
              </h2>
              <p className="text-gray-500 text-center mb-5">
                Fast response within 2 hours
              </p>

              {success ? (
                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-5 text-center">
                  <p className="font-semibold text-green-700">Quote request sent.</p>
                  <p className="mt-1 text-sm text-green-700">Our team will contact you soon.</p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    className="mt-4 text-sm font-semibold text-primary-500 hover:text-primary-600"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" noValidate>
                  {serverError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                      {serverError}
                    </div>
                  )}

                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full name"
                    className="form-input py-2.5"
                    required
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email"
                      className="form-input py-2.5"
                      required
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone"
                      className="form-input py-2.5"
                      required
                    />
                  </div>

                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="form-input py-2.5"
                    required
                  >
                    <option value="">Select service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.name}>
                        {service.name}
                      </option>
                    ))}
                  </select>

                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us what you need"
                    rows="3"
                    className="form-input resize-none py-2.5"
                    required
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full block text-center disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending...' : 'Request Free Quote'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;